/**
 * Shared vocabulary for the three Agent action variants — Native, Foundry,
 * and Coding. Centralizes the badge token pairs, glyph mapping, type-guard
 * composition, and the `defaultInputs` shape applied when a user picks a
 * variant. All UI surfaces (picker, command bar, canvas, node editor,
 * dotted edges) should derive their per-variant colors, icons, and inputs
 * from this module so the three variants stay visually + behaviourally in
 * lockstep.
 */

import { makeStyles } from '@fluentui/react-components';
import { BotFilled, BotSparkleFilled } from '@fluentui/react-icons';
import { CodingAgentGlyph } from '@/components/icons/CodingAgentGlyph';
import { ottoTokens } from '@/theme/ottoTokens';
import type { OperationDefinition } from '@/types/operations';
import type { Action } from '@/types/workflowDefinition';
import {
  isAgentAction,
  isCodingAgentAction,
  isCodingAgentHarnessType,
  isFoundryAgentAction,
  isFoundryAgentModelType,
} from '@/types/workflowDefinition';

/** True when the action is a FoundryHostedAgent (hosted container agent). */
const isHostedAgentAction = (action: Action): boolean => {
  if (!isAgentAction(action)) return false;
  const inputs = action.inputs;
  if (!inputs || typeof inputs !== 'object' || Array.isArray(inputs)) return false;
  const rec = inputs as Record<string, unknown>;
  if (rec.agentModelType === 'FoundryHostedAgent') return true;
  const params = rec.parameters;
  if (params && typeof params === 'object' && !Array.isArray(params)) {
    return (params as Record<string, unknown>).agentModelType === 'FoundryHostedAgent';
  }
  return false;
};

/** Visual + behavioural variant of the Agent action. */
export type AgentVariant = 'native' | 'foundry' | 'coding' | 'hosted';

/** All variants in the canonical UI ordering. */
export const AGENT_VARIANTS: readonly AgentVariant[] = ['native', 'foundry', 'coding', 'hosted'];

export interface AgentBadgeTokens {
  background: string;
  foreground: string;
}

const AGENT_BADGE_TOKENS: Record<AgentVariant, AgentBadgeTokens> = {
  native: {
    background: ottoTokens.agentNativeBadgeBackground,
    foreground: ottoTokens.agentNativeBadgeForeground,
  },
  foundry: {
    background: ottoTokens.agentFoundryBadgeBackground,
    foreground: ottoTokens.agentFoundryBadgeForeground,
  },
  coding: {
    background: ottoTokens.agentCodingBadgeBackground,
    foreground: ottoTokens.agentCodingBadgeForeground,
  },
  hosted: {
    background: ottoTokens.agentHostedBadgeBackground,
    foreground: ottoTokens.agentHostedBadgeForeground,
  },
};

/** Background + foreground token pair for a given agent variant. */
export const getAgentBadgeTokens = (variant: AgentVariant): AgentBadgeTokens =>
  AGENT_BADGE_TOKENS[variant];

const AGENT_BRAND_COLORS: Record<AgentVariant, string> = {
  native: ottoTokens.agentNativeBrand,
  foundry: ottoTokens.agentFoundryBrand,
  coding: ottoTokens.agentCodingBrand,
  hosted: ottoTokens.agentHostedBrand,
};

/** Saturated brand identity color for a given agent variant. */
export const getAgentBrandColor = (variant: AgentVariant): string =>
  AGENT_BRAND_COLORS[variant];

// Underscore-separated so they survive `.replace(/\s+/g, '_')` in callers.
// Mirrors the variant labels in AgentTypeView (Workflow / Foundry prompt / Coding).
const AGENT_VARIANT_ACTION_NAMES: Record<AgentVariant, string> = {
  native: 'Workflow_Agent',
  foundry: 'Foundry_Agent',
  coding: 'Coding_Agent',
  hosted: 'Hosted_Agent',
};

/** Default base action-name seed for a given agent variant. */
export const getAgentVariantActionName = (variant: AgentVariant): string =>
  AGENT_VARIANT_ACTION_NAMES[variant];

export interface AgentOperationSelection {
  variant: AgentVariant;
  /** Connection reference key to pre-populate as `modelConfigurations.model1.referenceName`. */
  connectionRefName?: string;
}

/**
 * Builds the runtime `OperationDefinition` for an agent variant from the
 * shared `AGENT_OPERATIONS[0]` base — applies the variant action name,
 * variant defaults, and optional connection pre-fill in one place so every
 * picker call site stays in lockstep.
 */
export const buildAgentOperationFromSelection = (
  baseOp: OperationDefinition,
  selection: AgentOperationSelection,
): OperationDefinition => {
  const { variant, connectionRefName } = selection;
  const baseInputs = (baseOp.defaultInputs ?? {}) as Record<string, unknown>;
  const withVariant = applyAgentVariantDefaults(baseInputs, variant);
  const defaultInputs: Record<string, unknown> = connectionRefName
    ? {
        ...withVariant,
        modelConfigurations: {
          ...((withVariant.modelConfigurations as Record<string, unknown> | undefined) ?? {}),
          model1: { referenceName: connectionRefName },
        },
      }
    : withVariant;
  return {
    ...baseOp,
    name: getAgentVariantActionName(variant),
    defaultInputs,
  };
};

/** Resolves the variant of an Action — `null` for non-agent actions. */
export const getAgentVariant = (action: Action): AgentVariant | null => {
  if (!isAgentAction(action)) return null;
  if (isHostedAgentAction(action)) return 'hosted';
  if (isCodingAgentAction(action)) return 'coding';
  if (isFoundryAgentAction(action)) return 'foundry';
  return 'native';
};

/**
 * Same as `getAgentVariant` but for components that only have the raw
 * `agentModelType` / `agentHarnessType` strings — e.g. canvas nodes that
 * read from flattened `data`, or sub-workflow previews assembling fresh
 * graph nodes.
 */
export const getAgentVariantFromTypes = (
  modelType: string | undefined,
  harnessType: string | undefined,
): AgentVariant => {
  if (modelType === 'FoundryHostedAgent') return 'hosted';
  if (isCodingAgentHarnessType(harnessType)) return 'coding';
  if (isFoundryAgentModelType(modelType)) return 'foundry';
  return 'native';
};

/**
 * Merges variant-specific defaults into a base Agent operation's
 * `defaultInputs`:
 *   - native  → unchanged
 *   - foundry → `agentModelType: 'FoundryAgentServiceV2'`
 *   - coding  → `agentModelType: 'AzureOpenAI'` + nested
 *               `parameters.agentModelSettings.agentHarness.type: 'GHCP'`
 *   - hosted  → `agentModelType: 'FoundryHostedAgent'`
 */
export const applyAgentVariantDefaults = (
  baseInputs: Record<string, unknown>,
  variant: AgentVariant,
): Record<string, unknown> => {
  if (variant === 'hosted') {
    return { ...baseInputs, agentModelType: 'FoundryHostedAgent' };
  }
  if (variant === 'foundry') {
    return { ...baseInputs, agentModelType: 'FoundryAgentServiceV2' };
  }
  if (variant === 'coding') {
    const params = (baseInputs.parameters as Record<string, unknown> | undefined) ?? {};
    const settings = (params.agentModelSettings as Record<string, unknown> | undefined) ?? {};
    return {
      ...baseInputs,
      agentModelType: 'AzureOpenAI',
      parameters: {
        ...params,
        agentModelSettings: {
          ...settings,
          agentHarness: { type: 'GHCP' },
        },
      },
    };
  }
  return baseInputs;
};

const useAgentBadgeClasses = makeStyles({
  native: {
    backgroundColor: ottoTokens.agentNativeBadgeBackground,
    color: ottoTokens.agentNativeBadgeForeground,
    '@media (forced-colors: active)': {
      backgroundColor: 'Highlight',
      color: 'HighlightText',
    },
  },
  foundry: {
    backgroundColor: ottoTokens.agentFoundryBadgeBackground,
    color: ottoTokens.agentFoundryBadgeForeground,
    '@media (forced-colors: active)': {
      backgroundColor: 'Highlight',
      color: 'HighlightText',
    },
  },
  coding: {
    backgroundColor: ottoTokens.agentCodingBadgeBackground,
    color: ottoTokens.agentCodingBadgeForeground,
    '@media (forced-colors: active)': {
      backgroundColor: 'Highlight',
      color: 'HighlightText',
    },
  },
  hosted: {
    backgroundColor: ottoTokens.agentHostedBadgeBackground,
    color: ottoTokens.agentHostedBadgeForeground,
    '@media (forced-colors: active)': {
      backgroundColor: 'Highlight',
      color: 'HighlightText',
    },
  },
});

/**
 * Returns a `(variant) => className` lookup for the pastel badge
 * background + foreground pair, with a `forced-colors` fallback.
 */
export const useAgentBadgeClass = (): ((variant: AgentVariant) => string) => {
  const classes = useAgentBadgeClasses();
  return (variant) => classes[variant];
};

const useAgentIconForegroundClasses = makeStyles({
  native: {
    color: ottoTokens.agentNativeBadgeForeground,
    '@media (forced-colors: active)': {
      color: 'CanvasText',
    },
  },
  foundry: {
    color: ottoTokens.agentFoundryBadgeForeground,
    '@media (forced-colors: active)': {
      color: 'CanvasText',
    },
  },
  coding: {
    color: ottoTokens.agentCodingBadgeForeground,
    '@media (forced-colors: active)': {
      color: 'CanvasText',
    },
  },
  hosted: {
    color: ottoTokens.agentHostedBadgeForeground,
    '@media (forced-colors: active)': {
      color: 'CanvasText',
    },
  },
});

/**
 * Returns a `(variant) => className` lookup for the variant glyph color
 * (foreground only), with a `forced-colors` fallback. Use when the
 * surrounding container already paints its own background (e.g. the
 * brand-color tile in the node editor header).
 */
export const useAgentIconForegroundClass = (): ((variant: AgentVariant) => string) => {
  const classes = useAgentIconForegroundClasses();
  return (variant) => classes[variant];
};

/** Glyph for the given agent variant. */
export const AgentVariantIcon = ({ variant }: { variant: AgentVariant }) => {
  if (variant === 'coding') return <CodingAgentGlyph />;
  if (variant === 'foundry') return <BotSparkleFilled />;
  if (variant === 'hosted') return <BotSparkleFilled />;
  return <BotFilled />;
};
